import { initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import {
  browserLocalPersistence,
  deleteUser,
  getAuth,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  setPersistence,
  signInAnonymously,
  type Auth,
  type Persistence,
} from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";
import { resolveAuthAction } from "./resolveAuthAction.js";

export interface FirebaseHandle {
  app: FirebaseApp;
  db: Firestore;
  /** authStateReady를 끝낸 Auth 인스턴스 — 계정 연동(AccountLinkProvider)이 currentUser race 없이 재사용. */
  auth: Auth;
  /** 익명 인증 UID. 인증 실패(오프라인/설정 오류) 시 null — 클라우드 세이브 비활성, 로컬 단독으로 진행. */
  uid: string | null;
  /** GA4 Analytics — measurementId 미설정/미지원(비웹) 시 null. */
  analytics: Analytics | null;
}

/**
 * Firebase 앱 초기화 + 익명 인증 + (가능 시) Analytics. 게임 부팅 시 1회 호출해 UID/Analytics를 확보한다.
 * 익명 인증/Analytics가 실패해도 throw하지 않는다 — 오프라인에서도 게임은 로컬 세이브로 부팅된다.
 */
export async function initFirebase(options: FirebaseOptions): Promise<FirebaseHandle> {
  const app = initializeApp(options);
  const db = getFirestore(app);
  // auth는 try 밖에서 확보 — getAuth는 throw하지 않으며, FirebaseHandle.auth로 노출해 계정 연동이 재사용한다.
  const auth = getAuth(app);

  let uid: string | null = null;
  try {
    // persistence 명시 — 기본값(웹=IndexedDB)에 의존하지 않고, 차단 환경(WebView/시크릿)에서 순차 폴백.
    // 이게 유지돼야 재부팅 시 기존 익명 UID가 복구돼 신규 계정 생성을 막는다.
    await applyPersistence(auth);
    // authStateReady() 전엔 currentUser가 항상 null이므로 반드시 await 후 판정(재사용 가드 race 방지).
    await auth.authStateReady();
    if (resolveAuthAction(auth.currentUser) === "reuse" && auth.currentUser) {
      uid = auth.currentUser.uid; // 기존 익명 유저 재사용 — 신규 계정 생성 skip
    } else {
      const cred = await signInAnonymously(auth);
      uid = cred.user.uid;
    }
  } catch (error) {
    console.warn(
      "[firebase] anonymous sign-in failed — local-only save:",
      (error as Error).message,
    );
  }

  let analytics: Analytics | null = null;
  try {
    if (options.measurementId && (await isSupported())) {
      analytics = getAnalytics(app);
    }
  } catch (error) {
    console.warn("[firebase] analytics init skipped:", (error as Error).message);
  }

  return { app, db, auth, uid, analytics };
}

/**
 * Auth persistence를 우선순위대로 시도 — 첫 성공에서 멈춘다. 환경별 차단(WebView/시크릿 모드의
 * IndexedDB 차단)에 graceful: 모두 실패해도 throw하지 않는다(기본 동작으로 진행).
 */
async function applyPersistence(auth: ReturnType<typeof getAuth>): Promise<void> {
  const candidates: Persistence[] = [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    inMemoryPersistence,
  ];
  for (const p of candidates) {
    try {
      await setPersistence(auth, p);
      return;
    } catch {
      // 다음 후보로 폴백
    }
  }
}

/**
 * 계정 삭제 시 현재 익명 유저를 Firebase Auth에서 제거(orphan 방지). 비익명/미인증이면 no-op.
 *
 * ⚠ deleteUser는 Firestore deleteDoc처럼 네트워크 상황에 따라 서버 ack까지 오래 걸릴 수 있어,
 * 호출부(resetSave→scene.restart)를 막지 않도록 timeoutMs 후 무조건 resolve한다(절대 throw 안 함).
 * 따라서 호출부는 await 없이 fire-and-forget으로 사용한다.
 *
 * @param app 초기화된 FirebaseApp 인스턴스.
 * @param timeoutMs 삭제 요청 최대 대기 시간(ms). 기본 3000 — 이 시간이 지나면 삭제 완료 여부와
 *   무관하게 resolve해 호출부(scene.restart)가 차단되지 않는다.
 */
export async function deleteAnonymousUser(app: FirebaseApp, timeoutMs = 3000): Promise<void> {
  try {
    const user = getAuth(app).currentUser;
    if (!user?.isAnonymous) return;
    await Promise.race([
      // ⚠ deleteUser는 timeout보다 늦게 reject할 수 있다(requires-recent-login 등). race가 이미
      // timeout으로 settle된 뒤의 rejection은 unhandled promise rejection이 되므로 여기서 흡수한다.
      deleteUser(user).catch((e: unknown) => {
        console.warn("[firebase] deleteAnonymousUser failed:", (e as Error).message);
      }),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch (error) {
    console.warn("[firebase] deleteAnonymousUser skipped:", (error as Error).message);
  }
}
